import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import classes from "./styles.module.scss";

import { getSvgUrl } from "../../../utils/imageUtils";
import SearchField from "../../../components/searchField";
import { useDispatch, useSelector } from "react-redux";
import { fetchSidebarData } from "../../../redux/slices/sidebar";
import { getDashboardPath, getRoleBasedNavigationLink } from "../../../helper/roleBasedNavigation";
import { getRoleFromJWT, getUserNameFromJWT, getEntityTypeFromJWT } from "../../../utils/secureStorage";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

const MiniLogo = getSvgUrl("newBlackLogo");

const LeftMenuContainer = ({ expand, handleExpand }) => {

  const location = useLocation();
  const isAdminDashboard = location.pathname.startsWith("/admin/map-dashboard") || location.pathname.startsWith("/admin/retailer-map");
  const dispatch = useDispatch();
  const sidebarData = useSelector((state) => state.sidebar.data);
  const [search, setSearch] = useState("");
  const [openMain, setOpenMain] = useState(null);
  const [openNested, setOpenNested] = useState(null);

  // Get user info directly from jwt 
  const role = getRoleFromJWT();
  const entityType = getEntityTypeFromJWT();
  const userName = getUserNameFromJWT();

  // Get the appropriate dashboard path for the user's entityType/role
  const dashboardPath = getDashboardPath(role, entityType);

  // Dashboard is being migrated to come from the /sidebar API like every other
  // menu. Until every role's API response includes a DASHBOARD node, roles that
  const dashboardMenuFromApi = (sidebarData || []).find(
    (item) => item.menuCode === "DASHBOARD"
  );
  const otherMenus = (sidebarData || []).filter(
    (item) => item.menuCode !== "DASHBOARD"
  );

  useEffect(() => {
    dispatch(fetchSidebarData());
  }, [dispatch]);

  const toggleMain = (key) => {
    setOpenMain((prev) => (prev === key ? null : key));
    setOpenNested(null);
  };

  const toggleNested = (key) => {
    setOpenNested((prev) => (prev === key ? null : key));
  };

  // Function that will filter the menu
  const filterMenu = (menuItems, searchTerm) => {
    if (!searchTerm) return menuItems;

    const lowerSearch = searchTerm.toLowerCase();

    const matchString = (str) => str?.toLowerCase().includes(lowerSearch);

    const filterRecursive = (items) => {
      const result = [];

      for (const item of items) {
        // If it's a program (leaf)
        if (item.type === "program") {
          if (matchString(item.programName) || matchString(item.programCode)) {
            result.push(item);
          }
        }

        // If it's a menu with children
        if (item.type === "menu" && item.children) {
          const filteredChildren = filterRecursive(item.children);

          // If current menu matches OR any child matched
          if (
            matchString(item.menuDesc) ||
            matchString(item.menuCode) ||
            filteredChildren.length > 0
          ) {
            result.push({
              ...item,
              children: filteredChildren,
            });
          }
        }
      }

      return result;
    };

    return filterRecursive(menuItems);
  };

  const renderChildLinks = (children) => (
    <div className={classes.sub_sub_menu_Wrapper}>
      {children.map((child) => {
        const NavigationLink = getRoleBasedNavigationLink(
          child?.programCode,
          child?.menuCode,
          role,
          entityType
        );
        return (
          <NavLink
            key={child.programCode}
            className={({ isActive }) =>
              `${classes.sub_sub_menu_text} ${isActive ? classes.active_sub_sub_menu_text : ""
                }`.trim()
            }
            to={NavigationLink}
          >
            {child.programName}
          </NavLink>
        );
      })}
    </div>
  );

  const renderNestedMenu = (item, index) => {
    const hasChildren = item.children && item.children.length > 0;
    const NavigationLink = getRoleBasedNavigationLink(
      item?.programCode,
      item?.menuCode,
      role,
      entityType
    );
    if (!hasChildren) {
      return (
        <NavLink
          key={index + "i"}
          className={({ isActive }) =>
            `${classes.sub_menu_text} ${isActive ? classes.active_sub_menu_text : ""
              }`.trim()
          }
          to={NavigationLink}
        >
          {item.programName}
        </NavLink>
      );
    }

    return (
      <div key={item.menuCode} className={classes.sub_menu_wrapper}>
        <button
          className={`
            ${classes.sub_menu_text_btn}
            ${openNested === item.menuCode
              ? classes.active_sub_menu_text_btn
              : ""
            }
          `}
          onClick={() => toggleNested(item.menuCode)}
        >
          <p className={classes.left_name}>{item.menuDesc}</p>
          <img src={getSvgUrl("down")} alt="icon" className={classes.arrow_down} />
        </button>
        {openNested === item.menuCode && renderChildLinks(item.children)}
      </div>
    );
  };

  const renderMainMenu = (main, index) => {
    const hasChildren = main.children && main.children.length > 0;
    const isDirectLink = main.menuLink === 1 && main.menuCode === "DASHBOARD";

    if (isDirectLink) {
      return (
        <div key={index + "i"} className={classes.menu_two}>
          <div
            className={`
              ${classes.menu_name}
              ${openMain === main.menuCode ? classes.active_menu_name : ""}
            `}
          >
            <NavLink
              to={dashboardPath}
              end
              className={classes.left_text_icon}
            >
              <img src={getSvgUrl("dashboard")} alt="icon" className={classes.left_icon} />
              <span className={classes.left_text}>{main.menuDesc}</span>
            </NavLink>
            {hasChildren && (
              <button
                type="button"
                className={classes.menu_toggle_btn}
                onClick={() => toggleMain(main.menuCode)}
                aria-label={`Toggle ${main.menuDesc} submenu`}
              >
                <img src={getSvgUrl("down")} alt="icon" className={classes.arrow_down} />
              </button>
            )}
          </div>

          {openMain === main.menuCode && hasChildren && (
            <div className={classes.sub_menu_list_wrapper}>
              {main.children.map(renderNestedMenu)}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={index + "i"} className={classes.menu_two}>
        <button
          className={`
            ${classes.menu_name}
            ${openMain === main.menuCode ? classes.active_menu_name : ""}
          `}
          onClick={() => toggleMain(main.menuCode)}
        >
          <p className={classes.left_text_icon}>
            <img src={getSvgUrl("dataManage")} alt="icon" className={classes.left_icon} />
            <span className={classes.left_text}>{main.menuDesc}</span>
          </p>
          <img src={getSvgUrl("down")} alt="icon" className={classes.arrow_down} />
        </button>

        {openMain === main.menuCode && hasChildren && (
          <div className={classes.sub_menu_list_wrapper}>
            {main.children.map(renderNestedMenu)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`
        ${classes.left_menu_container}
        ${expand ? classes.expand : ""}
        ${isAdminDashboard ? classes.hide_on_map : ""}
      `}
    >
      <button className={classes.expand_button} onClick={handleExpand}>
        {expand ? (
          <ChevronRightRoundedIcon />
        ) : (
          <KeyboardArrowLeftRoundedIcon />
        )}
      </button>

      <div className={classes.top_section}>
        <div className={classes.top}>
          <img src={MiniLogo} alt="logo" className={classes.mini_logo} />
          <p className={classes.role_name}>
            <span className={classes.role}>{role}</span>
            <span className={classes.name}>{userName}</span>
          </p>
        </div>
      </div>

      <nav className={classes.nav_container}>
        <div className={classes.search_area}>
          <SearchField
            search={search}
            setSearch={setSearch}
            second_class={classes.mod_search}
          />
        </div>

        {!dashboardMenuFromApi && (
          <NavLink
            to={dashboardPath}
            end
            className={({ isActive }) =>
              `${classes.nav_link} ${isActive ? classes.active_link : ""}`.trim()
            }
          >
            <img src={getSvgUrl("dashboard")} alt="icon" className={classes.nav_icon} />
            <span>Dashboard</span>
          </NavLink>
        )}

        <div className={classes.menus_and_sub_menus_two}>
          {dashboardMenuFromApi && renderMainMenu(dashboardMenuFromApi, "dashboard")}
          {filterMenu(otherMenus, search).map(renderMainMenu)}
        </div>
      </nav>

      <div className={classes.bottom}>
        {role?.toUpperCase() !== "UNION" && role?.toUpperCase() !== "DEPTADMIN" && (
          <NavLink
            to="/setting"
            className={({ isActive }) => (isActive ? classes.active_link : "")}
          >
            <img src={getSvgUrl("settings")} alt="icon" className={classes.nav_icon} />
            <span>Setting</span>
          </NavLink>
        )}
      </div>
    </div>
  );
};

LeftMenuContainer.propTypes = {
  expand: PropTypes.bool.isRequired,
  handleExpand: PropTypes.func.isRequired,
};

export default LeftMenuContainer;
